import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import Fitems from "./Fitems";
import "./InformationCenter.css";

const InformationCenter = () => {
  const baseVideoUrl = process.env.REACT_APP_SCAPI;
  const largeImgUrl = process.env.REACT_APP_LARGE_THUMBNAIL_URL;
  const ROOT_URL = process.env.REACT_APP_API_URL;

  const [products, setProducts] = useState([]);
  const [fItems, setFitems] = useState([]);
  const [activeTab, setActiveTab] = useState("");

  const [modal, setModal] = useState(false);
  const [fitemId, setFitemId] = useState("");
  const [itemPath, setItemPath] = useState("");

  // Loading + error
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [error, setError] = useState(null);

  const authHeader = useMemo(() => {
    const token = sessionStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Authorization: token || "",
    };
  }, []);

  const getSessionAuthData = useCallback(() => {
    const private_placement_id = parseInt(
      sessionStorage.getItem("privatePlacementId") || "0",
      10
    );
    const product_id = sessionStorage.getItem("productId") || "";
    const view_only = sessionStorage.getItem("viewOnly") || "";
    const profile_id = parseInt(sessionStorage.getItem("profileId") || "0", 10);

    return { private_placement_id, product_id, view_only, profile_id };
  }, []);

  const listFitems = useCallback(
    async (sectionId) => {
      if (!sectionId) return;

      setError(null);
      setActiveTab(sectionId);
      setIsLoadingItems(true);

      // Clear current list so UI doesn't show stale results under new tab
      setFitems([]);

      try {
        // Session cache hit?
        const cached = sessionStorage.getItem(String(sectionId));
        if (cached) {
          setFitems(JSON.parse(cached));
          return;
        }

        const { product_id, view_only, profile_id } = getSessionAuthData();

        const requestData = {
          view_only,
          product_id: parseInt(product_id || "0", 10),
          profile_id,
          investor_portal_section_id: sectionId,
        };

        const spFIInput = {
          spName: "ProductDataVault_ListSectionFItems",
          jsonInput: JSON.stringify(requestData),
        };

        const response = await axios({
          method: "post",
          url: ROOT_URL,
          data: JSON.stringify(spFIInput),
          headers: authHeader,
        });

        const parsed = JSON.parse(response.data);
        const rows = parsed?.Rows ?? [];

        setFitems(rows);
        sessionStorage.setItem(String(sectionId), JSON.stringify(rows));
      } catch (e) {
        console.log("SP Call Error:", e);
        setError("Unable to load items for this section. Please try again.");
      } finally {
        setIsLoadingItems(false);
      }
    },
    [ROOT_URL, authHeader, getSessionAuthData]
  );

  const listProducts = useCallback(async () => {
    setError(null);
    setIsLoadingProducts(true);

    try {
      const { private_placement_id, product_id, view_only, profile_id } =
        getSessionAuthData();

      const AuthData = {
        is_investor_portal: true,
        product_id,
        profile_id,
        private_placement_id,
        view_only,
      };

      const spInput = {
        spName: "ProductDataVault_ListSections",
        jsonInput: JSON.stringify(AuthData),
      };

      const response = await axios({
        method: "post",
        url: ROOT_URL,
        data: JSON.stringify(spInput),
        headers: authHeader,
      });

      const parsed = JSON.parse(response.data);
      const rows = parsed?.Rows ?? [];

      setProducts(rows);

      // Default to first tab on load
      if (rows.length > 0) {
        await listFitems(rows[0].investor_portal_section_id);
      } else {
        setActiveTab("");
        setFitems([]);
      }
    } catch (e) {
      console.log("SP Call Error:", e);
      setError("Unable to load sections. Please refresh and try again.");
    } finally {
      setIsLoadingProducts(false);
    }
  }, [ROOT_URL, authHeader, getSessionAuthData, listFitems]);

  useEffect(() => {
    listProducts();
  }, [listProducts]);

  const handleClick = useCallback((e, itemSku) => {
    e.preventDefault();
    itemPathRequest(itemSku);
  }, []);

  const itemPathRequest = useCallback(
    async (sku) => {
      setError(null);
      setIsLoadingModal(true);

      try {
        const { private_placement_id, product_id, view_only, profile_id } =
          getSessionAuthData();

        const investor_mode = sessionStorage.getItem("investorMode") || "";

        const requestData = {
          item_sku: sku,
          investor_mode,
          view_only,
          product_id,
          private_placement_id,
          profile_id,
        };

        const spRequest = {
          spName: "InvestorPortal_SelectFItem",
          jsonInput: JSON.stringify(requestData),
        };

        const response = await axios({
          method: "post",
          url: ROOT_URL,
          data: JSON.stringify(spRequest),
          headers: authHeader,
        });

        const parsed = JSON.parse(response.data);
        const row = parsed?.Rows?.[0];

        if (!row) throw new Error("No item returned");

        setItemPath(row.document_path || "");
        setFitemId(row.fitem_id || "");
        setModal(true);
      } catch (e) {
        console.log(e);
        setError("Unable to load media. Please try again.");
      } finally {
        setIsLoadingModal(false);
      }
    },
    [ROOT_URL, authHeader, getSessionAuthData]
  );

  const closeModal = useCallback(() => {
    setModal(false);
    setItemPath("");
    setFitemId("");
  }, []);

  const renderModal = () => {
    const videoPath = encodeURI(itemPath || "");
    const videoSrc = baseVideoUrl + videoPath;
    const poster = `${largeImgUrl}${fitemId}`;

    return (
      <div id="myModal" className={modal ? "modal-show" : "modal-hide"}>
        <div className="modal-content-cust">
          <div className="modal-header-cust">
            <span className="modal-close-cust" onClick={closeModal}>
              &times;
            </span>
            <h5 className="fw-4">Media Player</h5>
          </div>

          <div className="modal-body-cust">
            {isLoadingModal ? (
              <div className="ic-loader-center">
                <div className="ic-loader" />
                <div className="ic-loader-text">Loading media…</div>
              </div>
            ) : (
              <video
                className="modal-video-mod"
                controls
                key={videoSrc}
                autoPlay
                poster={poster}
              >
                <source src={videoSrc} type="video/mp4" />
                <source src={videoSrc} type="video/ogg" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </div>
      </div>
    );
  };

  const productTabs = products.map((p) => (
    <div
      key={p.investor_portal_section_id}
      className={
        activeTab === p.investor_portal_section_id
          ? "product_tab_active"
          : "product_tab"
      }
      onClick={() => listFitems(p.investor_portal_section_id)}
    >
      <span className="tab-item-text">{p.section_name}</span>
    </div>
  ));

  const fItemsArray = fItems.map((it) => (
    <Fitems
      key={it.item_sku}
      fitemId={it.fitem_id}
      itemSku={it.item_sku}
      itemName={it.item_name}
      isVideo={it.is_video}
      isHosted={it.hosted_video}
      downloadUrl={it.download_url}
      modalClick={(e) => handleClick(e, it.item_sku)}
    />
  ));

  return (
    <div className="container-padding" id="information-center">
      <h1 className="section-title-gray">information center</h1>

      {error && <div className="ic-error">{error}</div>}

      <div className="content_wrapper">
        {/* Tabs */}
        <div className="product_tab_wrapper">
          {isLoadingProducts ? (
            <div className="ic-loader-inline">
              <div className="ic-loader" />
              <span className="ic-loader-text">Loading sections…</span>
            </div>
          ) : (
            productTabs
          )}
        </div>

        {/* Items */}
        <div className="fitem_wrapper">
          {isLoadingProducts || isLoadingItems ? (
            <div className="ic-loader-center">
              <div className="ic-loader" />
              <div className="ic-loader-text">Loading items…</div>
            </div>
          ) : (
            fItemsArray
          )}
        </div>

        {renderModal()}
      </div>
    </div>
  );
};

export default InformationCenter;
